import React from "react";
import Skeleton from "../ui/Skeleton";

export const AdsCampaignsSkeleton: React.FC = () => {
  return (
    <div className="w-full animate-pulse px-1">
      <div>
        {/* KPI Summary Skeleton (4 Cards) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white/60 rounded-xl p-4 border border-purple-100 shadow-xs flex flex-col justify-between h-[104px]"
            >
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <div className="flex flex-col min-w-0 mt-0.5 space-y-2 flex-1">
                  <Skeleton variant="text" width="60%" height="0.6rem" />
                  <Skeleton variant="text" width="80%" height="1.5rem" />
                </div>
                <Skeleton
                  variant="rectangular"
                  width={36}
                  height={36}
                  className="rounded-[10px]"
                />
              </div>
              <Skeleton
                variant="rectangular"
                width="100%"
                height="1.5rem"
                className="rounded-lg opacity-50"
              />
            </div>
          ))}
        </div>

        {/* Action Bar Skeleton - matches the full-bleed sticky filter bar */}
        <div className="-mx-4 px-4 py-1.5 mb-4 border-b border-purple-200 bg-white/95 flex flex-col gap-3">
          <div className="flex flex-row flex-wrap gap-2 items-center">
            <Skeleton
              variant="rectangular"
              width="100%"
              height="2.25rem"
              className="rounded-xl flex-1 min-w-[200px]"
            />
            <Skeleton
              variant="rectangular"
              width="190px"
              height="2.5rem"
              className="rounded-xl shrink-0"
            />
            <Skeleton
              variant="rectangular"
              width="180px"
              height="2.5rem"
              className="rounded-xl shrink-0"
            />
            <Skeleton
              variant="rectangular"
              width="175px"
              height="2.5rem"
              className="rounded-xl shrink-0"
            />
            <Skeleton
              variant="rectangular"
              width="195px"
              height="2.5rem"
              className="rounded-xl shrink-0"
            />
            <Skeleton
              variant="rectangular"
              width="36px"
              height="36px"
              className="rounded-xl shrink-0"
            />
            <Skeleton
              variant="rectangular"
              width="76px"
              height="40px"
              className="rounded-xl shrink-0"
            />
          </div>
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-white/50 border border-purple-200 rounded-3xl shadow-xl shadow-purple-500/5 min-h-[400px] flex flex-col overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="bg-white border-b border-gray-200">
                <th className="w-10 px-3 py-3 text-center">
                  <Skeleton
                    variant="rectangular"
                    width="16px"
                    height="16px"
                    className="rounded-sm mx-auto"
                  />
                </th>
                <th className="w-[300px] px-2 py-3">
                  <Skeleton variant="text" width="80px" height="1rem" />
                </th>
                <th className="px-4 py-3 w-[180px]">
                  <Skeleton
                    variant="text"
                    width="90px"
                    height="1rem"
                    className="ml-auto"
                  />
                </th>
                <th className="px-4 py-3 w-[140px]">
                  <Skeleton
                    variant="text"
                    width="70px"
                    height="1rem"
                    className="ml-auto"
                  />
                </th>
                <th className="px-4 py-3 w-[120px]">
                  <Skeleton
                    variant="text"
                    width="70px"
                    height="1rem"
                    className="ml-auto"
                  />
                </th>
                <th className="w-[100px] px-2 py-3 text-center">
                  <Skeleton
                    variant="text"
                    width="50px"
                    height="1rem"
                    className="mx-auto"
                  />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  {/* Checkbox */}
                  <td className="px-3 py-4 text-center">
                    <Skeleton
                      variant="rectangular"
                      width="16px"
                      height="16px"
                      className="rounded-sm mx-auto"
                    />
                  </td>

                  {/* Anúncio */}
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      <Skeleton
                        variant="rectangular"
                        width="16px"
                        height="16px"
                        className="rounded-sm shrink-0"
                      />
                      <Skeleton
                        variant="rectangular"
                        width="40px"
                        height="40px"
                        className="rounded-lg shrink-0"
                      />
                      <div className="flex flex-col gap-1 w-full min-w-0">
                        <Skeleton
                          variant="text"
                          width="70%"
                          height="0.875rem"
                        />
                        <Skeleton
                          variant="text"
                          width="40%"
                          height="0.625rem"
                        />
                      </div>
                    </div>
                  </td>

                  {/* Performance */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1.5">
                      <Skeleton variant="text" width="40%" height="0.875rem" />
                      <Skeleton variant="text" width="60%" height="0.625rem" />
                    </div>
                  </td>

                  {/* Gastos */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1.5">
                      <Skeleton variant="text" width="50%" height="0.875rem" />
                      <Skeleton variant="text" width="40%" height="0.625rem" />
                    </div>
                  </td>

                  {/* Impressões */}
                  <td className="px-4 py-3 text-right">
                    <Skeleton
                      variant="text"
                      width="60%"
                      height="0.875rem"
                      className="ml-auto"
                    />
                  </td>

                  {/* Ações */}
                  <td className="px-2 py-3 align-middle">
                    <div className="flex h-full items-center justify-center gap-2">
                      <Skeleton
                        variant="rectangular"
                        width="32px"
                        height="32px"
                        className="rounded-xl"
                      />
                      <Skeleton
                        variant="rectangular"
                        width="32px"
                        height="32px"
                        className="rounded-xl"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdsCampaignsSkeleton;
